/**
 * TanStack Query hooks for /api/jobs reads.
 *
 * `useJobsApi()` returns the live backend job list. Polls every 2s so
 * the dashboard reflects near-live status changes.
 *
 * Statuses are translated here, once, from the API's stored vocabulary
 * to the UI's display vocabulary (see `@/lib/jobStatus`). Everything
 * downstream of this hook sees a UI `JobStatus`.
 *
 * `params.status`, if a caller ever passes it, is sent to the API as-is
 * and so must be an API status (`complete`, not `success`). No caller
 * passes it today; the dashboard filters client-side.
 */
import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/apiClient";
import { withUiStatus } from "@/lib/jobStatus";
import type { Job } from "@/lib/mockData";

/** A job as the API sends it: same shape, but `status` is a raw string. */
type ApiJob = Omit<Job, "status"> & { status: string };

interface ApiJobsResponse {
  items: ApiJob[];
  total: number;
}

interface JobsResponse {
  items: Job[];
  total: number;
}

export function normalizeJobsResponse(response: ApiJobsResponse): JobsResponse {
  return { ...response, items: response.items.map((job) => withUiStatus(job)) };
}

export function useJobsApi(params?: { entity?: string; status?: string }) {
  return useQuery({
    queryKey: ["jobs", params ?? null],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params?.entity && params.entity !== "all") q.set("entity", params.entity);
      if (params?.status && params.status !== "all") q.set("status", params.status);
      const suffix = q.toString() ? `?${q.toString()}` : "";
      return normalizeJobsResponse(await apiFetch<ApiJobsResponse>(`/jobs${suffix}`));
    },
    refetchInterval: 2_000,
    staleTime: 1_000,
    retry: 1,
  });
}
